import React, { Component, ErrorInfo, ReactNode } from 'react';
import { CUT_TT, UNEXPECTED_ERROR } from 'src/constants/strings';
import { TICKETING_PATH } from 'src/constants/routes';
import Alert from '@amzn/meridian/alert';
import Link from '@amzn/meridian/link';
import logger from '@utils/logger';
import Toaster from '@amzn/meridian/toaster';
import { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import { AlertType } from '@amzn/meridian/alert/alert';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  toasts: ToasterToasts[];
}

class ErrorBoundary extends Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      toasts: [],
    } as State;
  }

  static getDerivedStateFromError(e: Error): State {
    // Update state so the next render will show the fallback UI.
    if (e) {
      return { hasError: true, toasts: [{ id: 'error', timeout: 0 }] };
    }
    return { hasError: false, toasts: [] };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    void logger.error(`Uncaught error: ${error.name}`, errorInfo);
  }

  public render() {
    const { hasError, toasts } = this.state;
    const onClose = () => {
      this.setState({
        toasts: [],
        hasError: false,
      });
    };
    if (hasError) {
      return (
        <Toaster toasts={toasts} onCloseToast={onClose} data-cy="error_boundary">
          {(toast: ToasterToasts) => (
            <Alert type={toast.id as AlertType} onClose={onClose}>
              {UNEXPECTED_ERROR}{' '}
              <Link href={TICKETING_PATH} rel="noreferrer" target="_blank">
                {CUT_TT}
              </Link>
            </Alert>
          )}
        </Toaster>
      );
    }

    const { children } = this.props;
    return children;
  }
}

export default ErrorBoundary;
